// components/profile/ProfileHeader.tsx
import React from "react";
import {
  CheckCircle,
  Clock,
  XCircle,
  Mail,
  CreditCard,
  AlertCircle,
  LocateIcon,
} from "lucide-react";

interface Verification {
  emailVerified: boolean;
  addressVerified: boolean;
  ssnVerified: boolean;
  ssnVerificationStatus: string;
  // documentVerified: boolean;
  // documentVerificationStatus: string;
  // documentVerificationLink: string;
}

interface ProfileHeaderProps {
  verification: Verification;
  isDobFilled?: boolean;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ verification }) => {
  const badges = [
    {
      id: "email",
      label: "Email",
      verified: verification.emailVerified,
      icon: Mail,
    },
    {
      id: "address",
      label: "Address",
      verified: verification.addressVerified,
      icon: LocateIcon,
    },
    {
      id: "ssn",
      label: "SSN",
      verified: verification.ssnVerified,
      status: verification.ssnVerificationStatus,
      icon: CreditCard,
    },
    // {
    //   id: "document",
    //   label: "ID",
    //   verified: verification.documentVerified,
    //   status: verification.documentVerificationStatus,
    //   icon: FileText,
    // },
  ];

  const getStatusConfig = (verified: boolean, status?: string) => {
    if (verified) {
      return {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        icon: CheckCircle,
        iconColor: "text-emerald-600",
      };
    }
    if (status === "pending") {
      return {
        bg: "bg-amber-100",
        text: "text-amber-700",
        icon: Clock,
        iconColor: "text-amber-600",
      };
    }
    if (status === "failed") {
      return {
        bg: "bg-red-100",
        text: "text-red-700",
        icon: XCircle,
        iconColor: "text-red-600",
      };
    }
    return {
      bg: "bg-gray-100",
      text: "text-gray-500",
      icon: Clock,
      iconColor: "text-gray-400",
    };
  };

  const verifiedCount = badges.filter((b) => b.verified).length;
  const totalCount = badges.length;
  const allVerified = verifiedCount === totalCount;
  const hasPending = badges.some((b) => b.status === "pending");

  return (
    <div className="mb-6">
      {/* Main Header Card */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Left: Title & Description */}
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
              Profile
            </h1>
            <p className="text-gray-500 mt-1">
              Manage your personal information and verification status
            </p>
          </div>

          {/* Right: Verification Badges */}
          <div className="flex items-center gap-2">
            {badges.map((badge) => {
              const config = getStatusConfig(badge.verified, badge.status);
              return (
                <div
                  key={badge.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full ${config.bg} transition-all`}
                  title={`${badge.label}: ${badge.verified ? "Verified" : badge.status || "Not Verified"}`}
                >
                  <badge.icon className={`w-4 h-4 ${config.iconColor}`} />
                  <span
                    className={`text-sm font-medium ${config.text} hidden sm:inline`}
                  >
                    {badge.label}
                  </span>
                  <config.icon className={`w-3.5 h-3.5 ${config.iconColor}`} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Verification Alert - Only show if not all verified */}
        {!allVerified && (
          <div
            className={`mt-4 p-3 rounded-xl flex items-center justify-between ${
              hasPending
                ? "bg-amber-50 border border-amber-100"
                : "bg-gray-50 border border-gray-100"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  hasPending ? "bg-amber-100" : "bg-gray-200"
                }`}
              >
                <AlertCircle
                  className={`w-4 h-4 ${hasPending ? "text-amber-600" : "text-gray-500"}`}
                />
              </div>
              <div>
                <p
                  className={`text-sm font-medium ${hasPending ? "text-amber-800" : "text-gray-700"}`}
                >
                  {hasPending
                    ? "Verification in progress"
                    : `Complete your verification (${verifiedCount}/${totalCount})`}
                </p>
                <p
                  className={`text-xs ${hasPending ? "text-amber-600" : "text-gray-500"}`}
                >
                  {hasPending
                    ? "We're reviewing your submitted information. Keep an eye on your email."
                    : "Verify SSN to get jobs and receive payments"}
                </p>
              </div>
            </div>
            {/* {!hasPending && (
              <button className="text-sm font-medium text-[#8B52FF] hover:text-[#E05A6A] transition-colors flex items-center gap-1">
                Complete
                <ChevronRight className="w-4 h-4" />
              </button>
            )} */}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileHeader;
